import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSession } from 'src/contexts/SessionContext';
import {
  createNote,
  deleteNote,
  fetchNotes,
  updateNote,
} from 'src/models/note/note';
import { updatePlatform } from 'src/models/platform/platform';
import { updateUser } from 'src/models/user/user';
import { fetchUser } from 'src/models/user/user';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';
import * as Yup from 'yup';

import type { Note } from 'src/models/note/_types';
import type { Platform } from 'src/models/platform/_types';
import type { TrainingInfoField, User } from 'src/models/user/_types';

const perPage = 20;

export const useUserNotes = (userId) => {
  const queryClient = useQueryClient();
  const [noteToDelete, setNoteToDelete] = useState<Note | null>(null);
  const [noteToEdit, setNoteToEdit] = useState<Note | null>(null);
  const [fieldToEdit, setFieldToEdit] = useState<TrainingInfoField | null>(
    null
  );
  const [fieldToDelete, setFieldToDelete] = useState<TrainingInfoField | null>(
    null
  );
  const [platformFieldToEdit, setPlatformFieldToEdit] =
    useState<TrainingInfoField | null>(null);

  const {
    params,
    updateParam,
    filterParams,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams();
  const { page, sortBy: sort, sortDir } = params;

  const { data: user } = useQuery({
    queryKey: ['user', userId],

    queryFn: () => {
      if (!userId) {
        return;
      }
      return fetchUser({ id: userId });
    },

    enabled: !!userId,
  });

  const { session } = useSession();

  const { data: notes, isLoading: notesLoading } = useQuery({
    queryKey: [
      'userNotes',
      user?.id,
      page,
      perPage,
      sort,
      sortDir,
      filterParams,
    ],

    queryFn: () => {
      return fetchNotes({
        userId: user?.id,
        page: page,
        perPage,
        sort,
        sortDir,
        filters: {
          types: [filterParams.type],
          startDate: filterParams.date?.start,
          endDate: filterParams.date?.end,
        },
      });
    },

    enabled: !!user?.id,
  });

  const setSortBy = (sort) => {
    updateParam('sortBy', sort);
  };

  const setSortDir = (dir) => {
    updateParam('sortDir', dir);
  };

  const deleteNoteMutation = useMutation({
    mutationFn: (note: Note) => {
      return deleteNote(note);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userNotes'] });
      toast.success('Note successfully deleted');
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ['userNotes'] });
      toast.error(api.formatError(error, 'Note could not be deleted'));
    },
  });

  const createNoteMutation = useMutation({
    mutationFn: (note: Partial<Note>) => {
      return createNote(note);
    },
    onSuccess: () => {
      setNoteToEdit(null);
      queryClient.invalidateQueries({ queryKey: ['userNotes'] });
      toast.success('Note successfully created');
    },
    onError: (error) => {
      toast.error(api.formatError(error, 'Note could not be created'));
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: (note: Partial<Note>) => {
      return updateNote(note);
    },
    onSuccess: () => {
      setNoteToEdit(null);
      queryClient.invalidateQueries({ queryKey: ['userNotes'] });
      toast.success('Note updated');
    },
    onError: (error) => {
      toast.error(api.formatError(error, 'Note could not be updated'));
    },
  });

  const { mutateAsync } = noteToEdit?.id
    ? updateNoteMutation
    : createNoteMutation;

  const formik = {
    initialValues: {
      title: noteToEdit?.title || '',
      notes: noteToEdit?.notes || [],
      userId: noteToEdit?.userId || '',
      date: noteToEdit?.date || new Date(),
    },
    validationSchema: Yup.object({
      title: Yup.string().required().max(255),
      notes: Yup.string().required(),
      userId: Yup.string().required(),
      date: Yup.date().typeError('please enter a valid date').required(),
    }),
    onSubmit: async (values) => {
      return mutateAsync({ ...values, id: noteToEdit?.id });
    },
  };

  const handleFieldClose = () => {
    setFieldToDelete(null);
    setPlatformFieldToEdit(null);
  };

  const trainingInfoFields = session?.trainingInfoFields?.map((field) => {
    const userField = user?.trainingInfoFields?.find((f) => f.id === field.id);
    return { ...field, value: userField?.value || '' };
  });

  const userMutation = useMutation({
    mutationFn: (partialUser: Partial<User>) => {
      if (!user?.id) {
        throw new Error('Attempted user update without ID');
      }
      return updateUser(user?.id, partialUser);
    },
    onMutate: (partialUser) => {
      queryClient.setQueryData(['user', user?.id], { ...user, ...partialUser });
    },
    onSuccess: (partialUser) => {
      setFieldToEdit(null);
      queryClient.setQueryData(['user', user?.id], { ...partialUser });
      toast.success('Note Updated');
    },
    onError: (error) => {
      toast.error(api.formatError(error, 'Notes could not be updated'));
    },
  });

  const userFormik = {
    initialValues: {
      id: fieldToEdit?.id || '',
      value: fieldToEdit?.value || '',
      label: fieldToEdit?.label || '',
    },
    validationSchema: Yup.object({
      id: Yup.string().required(),
      label: Yup.string().required(),
      value: Yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        const fieldsToSave = trainingInfoFields?.map((field) => {
          if (field.id === values.id) {
            return { ...field, value: values.value };
          }
          return { ...field };
        });

        await userMutation.mutateAsync({
          trainingInfoFields: fieldsToSave,
          id: user?.id,
        });
      } catch (error) {
        toast.error(api.formatError(error, 'Notes could not be updated'));
        throw error; // Re-throw to trigger formik's error handling
      }
    },
  };

  const platformMutation = useMutation({
    mutationFn: (platform: Partial<Platform>) => {
      return updatePlatform(session?.id, platform);
    },
    onMutate: (data) => {
      queryClient.setQueryData(['session'], { ...session, ...data });
      setFieldToDelete(null);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['session'], { ...data });
      setFieldToDelete(null);
      setPlatformFieldToEdit(null);
    },
  });

  const platformFormik = {
    initialValues: {
      id: platformFieldToEdit?.id || '',
      value: platformFieldToEdit?.value || '',
      label: platformFieldToEdit?.label || '',
    },
    validationSchema: Yup.object({
      id: Yup.string(),
      label: Yup.string().required(),
      value: Yup.string(),
    }),
    onSubmit: async (values) => {
      let newField = true;

      const fieldsToSave = (session.trainingInfoFields || []).map((field) => {
        if (values.id && field.id === values.id) {
          newField = false;

          return {
            ...values,
            id:
              field.id ||
              values.label
                .trim()
                .toLowerCase()
                .replace(/\s/g, '_')
                .replace(/\W/g, ''),
          };
        }
        return { ...field };
      });

      if (newField) {
        fieldsToSave.push({
          ...values,
          id:
            values.id ||
            values.label
              .trim()
              .toLowerCase()
              .replace(/\s/g, '_')
              .replace(/\W/g, ''),
        });
      }

      return platformMutation.mutateAsync({
        trainingInfoFields: fieldsToSave,
        id: session?.id,
      });
    },
  };

  const handleDeleteNote = () => {
    if (noteToDelete) {
      deleteNoteMutation.mutate(noteToDelete);
    }
    setNoteToDelete(null);
  };

  const handleDeleteField = () => {
    return platformMutation.mutateAsync({
      trainingInfoFields: (session.trainingInfoFields || [])
        .filter((f) => fieldToDelete?.id != f.id)
        .map((f) => ({ ...f })),
      id: session?.id,
    });
  };

  const handleClose = () => {
    setNoteToDelete(null);
    setNoteToEdit(null);
    setFieldToDelete(null);
    setFieldToEdit(null);
    setPlatformFieldToEdit(null);
  };

  return {
    notes: notes?.notes,
    totalEntries: notes?.total,
    notesLoading: notesLoading || !user?.id,
    noteToDelete,
    setNoteToDelete,
    noteToEdit,
    setNoteToEdit,
    user,
    formik,
    userFormik,
    fieldToEdit,
    setFieldToEdit,
    handleFieldClose,
    platformFieldToEdit,
    setPlatformFieldToEdit,
    platformFormik,
    fieldToDelete,
    setFieldToDelete,
    session,
    trainingInfoFields,
    platformMutation,
    page,
    perPage,
    sort,
    sortDir,
    filters: filterParams,
    handleDeleteField,
    handleDeleteNote,
    handleClose,
    setSortBy,
    setSortDir,

    params,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  };
};
